import React, { useState, useEffect } from "react";
import axios from "axios";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";
import PubvisionLogo from "../../assets/images/newlogo.png";
import LoginPageScreen from "../../assets/images/PubVisionCoverPage_26Feb.png";
import { useWebSockets } from "../../contexts/WebSocketContext";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { connectGlobalWebSocket } = useWebSockets();

  // Load remembered username on mount and clear old auth tokens
  useEffect(() => {
    // Clear any existing auth tokens to prevent stale token issues
    localStorage.removeItem("authToken");
    localStorage.removeItem("userData");
    localStorage.removeItem("organizationData");
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("userData");
    sessionStorage.removeItem("organizationData");
    
    const savedUsername = localStorage.getItem("rememberedUsername");
    if (savedUsername) {
      setEmail(savedUsername);
      setRememberMe(true);
    }
  }, []);

  // Preload the login image
  useEffect(() => {
    const img = new Image();
    img.src = LoginPageScreen;
    img.onload = () => setImageLoaded(true);
    // If image is already cached, it will load immediately
    if (img.complete) setImageLoaded(true);
  }, []);

  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => {
        setAlert(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [alert]);

  const login = async (e) => {
    e.preventDefault();
    
    // Get values directly from form to handle browser autofill properly
    const form = e.target;
    const usernameInput = form.querySelector('input[name="username"]');
    const passwordInput = form.querySelector('input[name="password"]');
    
    // Use form values if available (handles autofill), fallback to state
    const trimmedUsername = (usernameInput?.value || email).trim();
    const trimmedPassword = (passwordInput?.value || password).trim();
    
    const formData = new URLSearchParams();
    formData.append("username", trimmedUsername);
    formData.append("password", trimmedPassword);

    try {
      console.log("[Login] Attempting login with username:", trimmedUsername);
      console.log("[Login] Password length:", trimmedPassword.length);
      console.log("[Login] BASE_URL:", BASE_URL);
      
      // Create fresh axios instance to ensure no interceptors or cached headers
      const response = await axios.post(
        `${BASE_URL}/login`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          transformRequest: [(data) => data], // Prevent any transformation
          withCredentials: false, // Don't send cookies
        }
      );
      
      console.log("[Login] Response status:", response.status);
      console.log("[Login] Response data:", response.data);

      if (response.status === 200) {
        // Always use localStorage for auth data (app-wide expectation)
        // The login page already clears old tokens on mount, providing session control
        localStorage.setItem("authToken", response.data.access_token);
        // Store user and organization data for access control
        if (response.data.user) {
          localStorage.setItem("userData", JSON.stringify(response.data.user));
        }
        if (response.data.organization) {
          localStorage.setItem("organizationData", JSON.stringify(response.data.organization));
        }
        // Save or clear remembered username
        if (rememberMe) {
          localStorage.setItem("rememberedUsername", trimmedUsername);
        } else {
          localStorage.removeItem("rememberedUsername");
        }
        
        // Connect to global WebSocket using userId
        if (response.data.user?.userId) {
          console.log("[Login] Connecting to global WebSocket with userId:", response.data.user.userId);
          connectGlobalWebSocket(response.data.user.userId);
        }
        
        setAlert({
          severity: "success",
          message: "Successfully Logged In",
        });
        navigate("/Dashboard");
      } else {
        setAlert({
          severity: "error",
          message: "Unable to reach the server. Please try again.",
        });
      }
    } catch (error) {
      console.error("[Login] Error occurred:", error);
      console.error("[Login] Error response:", error.response);
      console.error("[Login] Error response data:", error.response?.data);
      console.error("[Login] Error response status:", error.response?.status);
      
      if (error.response) {
        const status = error.response.status;
        if (status === 401 || status === 404) {
          setAlert({
            severity: "error",
            message: "Invalid username or password.",
          });
        } else {
          setAlert({
            severity: "error",
            message: "Unable to reach the server. Please try again.",
          });
        }
      } else if (error.request) {
        setAlert({
          severity: "error",
          message: "Unable to reach the server. Please try again.",
        });
      } else {
        setAlert({
          severity: "error",
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  };


  // Show loading spinner while image is loading
  if (!imageLoaded) {
    return (
      <div style={styles.containerWrapper}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <img src={PubvisionLogo} alt="PubVision AI" style={{ height: '70px', marginBottom: '20px' }} />
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e0e0e0',
            borderTop: '3px solid #1e4de3',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div style={styles.containerWrapper}>
      <div style={styles.container}>
        <div style={styles.leftSection}>
          <img src={LoginPageScreen} alt="Login Screen" style={styles.loginImage} />
        </div>

        <div style={styles.rightSection}>
          <div style={styles.logoContainer}>
            <img src={PubvisionLogo} alt="PubVision AI" style={styles.logo} />
          </div>
          
          
          
          <form style={styles.form} onSubmit={login}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Username</label>
              <input
                type="text"
                name="username"
                autoComplete="username"
                placeholder="Username"
                style={styles.inputField}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            
            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <div style={styles.passwordWrapper}>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  autoComplete="current-password"
                  placeholder="Password"
                  style={styles.passwordInput}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.eyeIcon}
                  size="small"
                >
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </div>
            </div>

            <div style={styles.optionsRow}>
              <label style={styles.rememberMeLabel}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={styles.checkbox}
                />
                Remember Me
              </label>
            </div>

            <button type="submit" style={styles.loginButton}>
              Log In
            </button>
          </form>

          {alert && (
            <Stack sx={{width: "100%", maxWidth: "400px" }} spacing={2}>
              <Alert
                severity={alert.severity}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => setAlert(null)}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
                sx={{ mt: 2 }}
              >
                {alert.message}
              </Alert>
            </Stack>
          )}
        </div>
      </div>
      <style>
        {`
          body {
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden;
          }
          html {
            margin: 0 !important;
            padding: 0 !important;
          }
          input:focus {
            outline: none !important;
            border-color: #1e4de3 !important;
            box-shadow: 0 0 0 2px rgba(30, 77, 227, 0.1) !important;
          }
          input:hover {
            border-color: #1e4de3 !important;
          }
        `}
      </style>
    </div>
  );
};

// Added the CSS for the rotating animation
const styles = {
  containerWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#ffffff",
    padding: 0,
    margin: 0,
  },
  container: {
    display: "flex",
    width: "100%",
    height: "100%",
    fontFamily: "'Inter', sans-serif",
    overflow: "hidden",
    backgroundColor: "white",
    padding: 0,
    margin: 0,
  },
  leftSection: {
    width: "60%",
    display: "block",
    backgroundColor: "#ffffff",
    padding: 0,
    margin: 0,
    overflow: "hidden",
    position: "relative",
  },
  loginImage: {
    width: "110%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    position: "absolute",
    top: 0,
    left: "-5%",
  },
  rightSection: {
    width: "40%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px",
    backgroundColor: "#ffffff",
  },
  logoContainer: {
    marginBottom: "30px",
  },
  logo: {
    height: "70px",
    width: "auto",
  },
  welcomeTitle: {
    fontSize: "32px",
    fontWeight: "bold",
    color: "#000000",
    marginBottom: "10px",
    textAlign: "center",
  },
  subtitle: {
    fontSize: "14px",
    color: "#666666",
    marginBottom: "30px",
    textAlign: "center",
  },
  form: {
    width: "100%",
    maxWidth: "400px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#333333",
  },
  inputField: {
    padding: "12px 16px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
    fontSize: "14px",
    width: "100%",
    boxSizing: "border-box",
    fontFamily: "'Inter', sans-serif",
  },
  passwordWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  passwordInput: {
    padding: "12px 40px 12px 16px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
    fontSize: "14px",
    width: "100%",
    boxSizing: "border-box",
    fontFamily: "'Inter', sans-serif",
  },
  eyeIcon: {
    position: "absolute",
    right: "8px",
    color: "#666666",
  },
  optionsRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "-10px",
  },
  rememberMeLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    color: "#666666",
    cursor: "pointer",
  },
  checkbox: {
    cursor: "pointer",
  },
  forgotPasswordLink: {
    background: "none",
    border: "none",
    color: "#1e4de3",
    fontSize: "14px",
    cursor: "pointer",
    textDecoration: "none",
    padding: 0,
  },
  loginButton: {
    backgroundColor: "#1e4de3",
    color: "white",
    borderRadius: "8px",
    padding: "14px 20px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "500",
    width: "100%",
    border: "none",
    marginTop: "10px",
    fontFamily: "'Inter', sans-serif",
  },
};

export default Login;